import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const COMPANY_NAME = "Inew Technologies LLP";

function todayLong() {
  return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function prefillValue(key, employee, payroll) {
  const k = key.toLowerCase();
  if (k === "company_name") return COMPANY_NAME;
  if (k === "date") return todayLong();
  if (payroll && payroll[k] !== undefined && payroll[k] !== null) return String(payroll[k]);
  if (!employee) return "";
  const map = {
    name: employee.name, email: employee.email, designation: employee.designation,
    department: employee.department?.name, department_name: employee.department?.name,
    phone: employee.phone, address: employee.address, salary: employee.base_salary,
    base_salary: employee.base_salary, joining_date: employee.joining_date,
    employee_id: employee.id, manager: employee.manager_name, manager_name: employee.manager_name,
  };
  const v = map[k];
  return v === undefined || v === null ? "" : String(v);
}

export default function GenerateDocument() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const entityType = params.get("entityType") || "employee";
  const entityId = params.get("entityId");
  const payrollId = params.get("payrollId");
  const returnTo = params.get("returnTo") || (entityType === "employee" ? `/hrms/directory/${entityId}` : "/hrms/directory");

  const [templateId, setTemplateId] = useState(params.get("templateId") || "");
  const [values, setValues] = useState({});
  const [initializedFor, setInitializedFor] = useState(null);

  const { data: templates, isLoading: loadingTemplates } = useQuery({ queryKey: ["doc-templates", "hrms"], queryFn: () => get("/doc-templates?module=hrms") });
  const { data: employee } = useQuery({
    queryKey: ["employee", entityId],
    queryFn: () => get(`/hrms/employees/${entityId}`),
    enabled: entityType === "employee" && !!entityId,
  });
  const { data: payrollRows } = useQuery({
    queryKey: ["payroll", "employee", entityId],
    queryFn: () => get(`/hrms/payroll?employee_id=${entityId}`),
    enabled: !!payrollId && !!entityId,
  });
  const payroll = useMemo(() => (payrollRows || []).find((p) => p.id === payrollId) || null, [payrollRows, payrollId]);

  const template = (templates || []).find((t) => t.id === templateId) || null;
  const employeeReady = entityType !== "employee" || !!employee;
  const payrollReady = !payrollId || !!payroll;

  useEffect(() => {
    if (template && employeeReady && payrollReady && initializedFor !== template.id) {
      const initial = {};
      template.fields.forEach((f) => { initial[f.key] = prefillValue(f.key, employee, payroll); });
      setValues(initial);
      setInitializedFor(template.id);
    }
  }, [template, employee, payroll, employeeReady, payrollReady, initializedFor]);

  const generate = useMutation({
    mutationFn: (body) => post(`/doc-templates/${templateId}/generate`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["dms-files", entityType, entityId] });
      toast.success(res.pdf_generated ? "Document generated (DOCX + PDF)" : "Document generated (DOCX — PDF conversion unavailable on this server)");
      navigate(returnTo);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not generate document"),
  });

  if (!entityId) {
    return <EmptyState icon={FileText} title="No record selected" description="Open this page from an employee's Documents tab, Payroll, or an onboarding checklist." />;
  }

  if (!template) {
    return (
      <div className="max-w-2xl">
        <PageHeader title="Generate Document" subtitle="Choose a template to fill in and generate." />
        {loadingTemplates ? <Skeleton className="h-48" /> : !templates?.length ? (
          <EmptyState icon={FileText} title="No templates available" description="Ask an admin to upload a template under Document Templates first." />
        ) : (
          <Section>
            <div className="divide-y divide-border">
              {templates.map((t) => (
                <button key={t.id} data-testid={`pick-template-${t.id}`} onClick={() => setTemplateId(t.id)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-muted/50">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{t.name}</span></div>
                  <span className="text-xs text-muted-foreground">{t.fields.length} field{t.fields.length === 1 ? "" : "s"}{t.has_signature_marker ? " · signature" : ""}</span>
                </button>
              ))}
            </div>
          </Section>
        )}
        <div className="mt-4 flex justify-end">
          <Button variant="ghost" onClick={() => navigate(returnTo)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <PageHeader title={`Generate: ${template.name}`} subtitle="Review and edit the fields below, then generate the document." />
      <Section title="Fields">
        <div className="grid grid-cols-2 gap-4 p-4">
          {template.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
              <Label>{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`gen-field-${f.key}`} className="mt-1" value={values[f.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
              ) : (
                <Input data-testid={`gen-field-${f.key}`} type={f.type === "date" || f.type === "number" ? f.type : "text"} className="mt-1"
                  value={values[f.key] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        {template.has_signature_marker && (
          <p className="border-t border-border/60 px-4 py-2.5 text-xs text-muted-foreground">
            <Sparkles className="mr-1 inline h-3 w-3" />
            {template.signature_r2_key ? "The configured signature stamp will be added automatically." : "No signature image is configured for this template yet — it will be generated without one."}
          </p>
        )}
      </Section>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate(returnTo)}>Cancel</Button>
        <Button data-testid="generate-submit" disabled={generate.isPending}
          onClick={() => generate.mutate({ entity_type: entityType, entity_id: entityId, values })}>
          Generate Document
        </Button>
      </div>
    </div>
  );
}
